/**
 * IRR Calculator Utility
 * Calculates Internal Rate of Return for real estate investments
 */

/**
 * Calculate NPV (Net Present Value) for a given discount rate
 * @param {number} initialInvestment - Initial cash outflow (negative)
 * @param {number[]} cashFlows - Array of annual cash flows
 * @param {number} rate - Discount rate (as decimal, e.g., 0.1 for 10%)
 * @returns {number} Net Present Value
 */
function calculateNPV(initialInvestment, cashFlows, rate) {
  let npv = -initialInvestment; // Initial investment is negative

  for (let i = 0; i < cashFlows.length; i++) {
    npv += cashFlows[i] / Math.pow(1 + rate, i + 1);
  }

  return npv;
}

/**
 * Calculate IRR using Newton-Raphson method
 * @param {number} initialInvestment - Initial cash outflow
 * @param {number[]} cashFlows - Array of annual cash flows
 * @param {number} guess - Initial guess for IRR (default 0.1)
 * @param {number} tolerance - Convergence tolerance (default 0.0001)
 * @param {number} maxIterations - Maximum iterations (default 100)
 * @returns {number} IRR as percentage (e.g., 8.5 for 8.5%)
 */
export function calculateIRR(initialInvestment, cashFlows, guess = 0.1, tolerance = 0.0001, maxIterations = 100) {
  let rate = guess;

  for (let i = 0; i < maxIterations; i++) {
    const npv = calculateNPV(initialInvestment, cashFlows, rate);

    // Check for convergence
    if (Math.abs(npv) < tolerance) {
      return rate * 100; // Return as percentage
    }

    // Calculate derivative (NPV')
    let derivative = 0;
    for (let j = 0; j < cashFlows.length; j++) {
      derivative -= (j + 1) * cashFlows[j] / Math.pow(1 + rate, j + 2);
    }

    // Newton-Raphson update
    rate = rate - npv / derivative;

    // Prevent extreme values
    if (rate < -0.99) rate = -0.99;
    if (rate > 10) rate = 10;
  }

  // If not converged, return NaN
  return NaN;
}

/**
 * Generate year-by-year cash flow projection
 * @param {Object} params - Investment parameters
 * @returns {Object} Year-by-year projection and IRR
 */
export function generateCashFlowProjection(params) {
  const {
    propertyPrice,
    monthlyRent, // This should be the MAX of user input, ML, and AI predictions
    monthlyCarryingCosts,
    totalOutOfPocket, // New: Use Total Out of Pocket as initial investment
    monthlyMortgagePayment, // New: Use saved mortgage payment (for initial I/O period)
    mortgageRate = 7, // New: Use saved mortgage rate or default
    mortgageTerm = 30, // New: Use saved mortgage term or default (always 30 years)
    paymentType = "P&I", // New: I/O or P&I
    downPaymentPercent = 25, // New: Use saved down payment percent
    rentGrowthRate = 5, // Changed default from 4% to 5%
    propertyAppreciationRate = 6, // percent per year (hardcoded)
    years = 10,
    // Property management, charged as a percent of the rent actually collected and only from
    // managementFeeStartYear on -- the first year is self-managed while the unit is being set
    // up. 0 (the default) is "no fee", which keeps every existing caller unchanged.
    managementFeePercent = 0,
    managementFeeStartYear = 2,
  } = params;

  // Use Total Out of Pocket as initial investment
  const initialInvestment = totalOutOfPocket;

  // Calculate mortgage amount directly from property price and down payment
  const downPayment = propertyPrice * (downPaymentPercent / 100);
  const mortgageAmount = propertyPrice - downPayment;

  const monthlyInterestRate = mortgageRate / 100 / 12;
  const numberOfPayments = mortgageTerm * 12;

  const yearlyData = [];
  let currentPropertyValue = propertyPrice;
  const sellingCostPercent = 6.825; // Cost of selling the property

  // Track target cash flow improvement (starts at 0 for year 1)
  let targetCashFlowImprovement = 0;

  for (let year = 1; year <= years; year++) {
    // Mortgage payment stays constant for all 10 years (I/O throughout)
    let currentMonthlyMortgagePayment = monthlyMortgagePayment;

    // Calculate annual expenses
    const annualCarryingCosts = monthlyCarryingCosts * 12;
    const annualMortgagePayment = currentMonthlyMortgagePayment * 12;
    const totalAnnualExpenses = annualCarryingCosts + annualMortgagePayment;

    // Calculate Year 1 baseline cash flow (rent - expenses)
    const year1AnnualRent = monthlyRent * 12;
    const year1Expenses = (monthlyCarryingCosts * 12) + (monthlyMortgagePayment * 12);
    const year1CashFlow = year1AnnualRent - year1Expenses;

    // Apply cumulative improvement to Year 1 cash flow
    // For negative cash flows, improvement makes it less negative (better)
    // For positive cash flows, improvement makes it more positive (better)
    let annualCashFlow;
    if (year1CashFlow < 0) {
      // Negative cash flow: improve by reducing the loss
      annualCashFlow = year1CashFlow + targetCashFlowImprovement;
    } else {
      // Positive cash flow: improve by increasing the gain
      annualCashFlow = year1CashFlow + targetCashFlowImprovement;
    }

    // Back-calculate rent needed to achieve this cash flow
    const annualRentIncome = annualCashFlow + totalAnnualExpenses;
    const currentRent = annualRentIncome / 12;

    // Management fee, deducted AFTER the rent is known -- it is a percentage of what is
    // collected, so it has to come off the back-calculated rent rather than being folded into
    // the expense base the rent is solved against. Year 1 is exempt.
    const managementFee = year >= managementFeeStartYear
      ? annualRentIncome * (managementFeePercent / 100)
      : 0;
    annualCashFlow -= managementFee;

    // Calculate property appreciation
    const yearStartValue = currentPropertyValue;
    currentPropertyValue = currentPropertyValue * (1 + propertyAppreciationRate / 100);
    const appreciation = currentPropertyValue - yearStartValue;

    // Calculate mortgage balance at end of year
    // For I/O mortgages, balance stays constant (no principal paid)
    // For P&I mortgages, calculate remaining balance
    let mortgageBalance = 0;
    if (mortgageAmount > 0) {
      if (paymentType === "I/O") {
        // Interest Only: balance never changes
        mortgageBalance = mortgageAmount;
      } else {
        // P&I: calculate remaining balance using amortization formula
        const paymentsToDate = year * 12;
        const remainingPayments = numberOfPayments - paymentsToDate;

        if (remainingPayments > 0) {
          mortgageBalance = currentMonthlyMortgagePayment * (Math.pow(1 + monthlyInterestRate, remainingPayments) - 1) /
            (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, remainingPayments));
        }
      }
    }

    // Calculate equity (property value - mortgage balance)
    // In final year, subtract selling costs from property value
    let equity;
    if (year === years) {
      const netPropertyValue = currentPropertyValue * (1 - sellingCostPercent / 100);
      equity = netPropertyValue - mortgageBalance;
    } else {
      equity = currentPropertyValue - mortgageBalance;
    }

    // What an exit IN THIS YEAR actually hands back: value, less the cost of selling, less the
    // debt. `equity` above is only net of selling costs in the FINAL year, which is right for
    // the projection's own IRR and wrong for anyone reading an earlier year as an exit -- the
    // IRR report's 7-Year Exit panel did exactly that out of a 10-year run and overstated the
    // return by 36 points. Published per year so no caller has to remember the rule.
    const equityIfSold = currentPropertyValue * (1 - sellingCostPercent / 100) - mortgageBalance;

    // For IRR calculation, we only use cash flow (not paper appreciation)
    // Unless it's the final year where we "sell" the property
    const cashFlowForIRR = year === years
      ? annualCashFlow + equity // Final year includes equity from sale (after selling costs)
      : annualCashFlow;

    yearlyData.push({
      year,
      rent: Math.round(currentRent),
      annualRentIncome: Math.round(annualRentIncome),
      annualCarryingCosts: Math.round(annualCarryingCosts),
      annualMortgagePayment: Math.round(annualMortgagePayment),
      totalExpenses: Math.round(totalAnnualExpenses + managementFee),
      managementFee: Math.round(managementFee),
      cashFlow: Math.round(annualCashFlow),
      propertyValue: Math.round(currentPropertyValue),
      appreciation: Math.round(appreciation),
      mortgageBalance: Math.round(mortgageBalance),
      equity: Math.round(equity),
      equityIfSold: Math.round(equityIfSold),
      cashFlowForIRR: Math.round(cashFlowForIRR),
    });

    // Improve cash flow for next year by the growth rate
    // This accumulates: each year adds 5% of the current improved cash flow
    const currentImprovedCashFlow = year1CashFlow + targetCashFlowImprovement;
    const improvementAmount = Math.abs(currentImprovedCashFlow) * (rentGrowthRate / 100);
    targetCashFlowImprovement += improvementAmount;
  }

  // Calculate IRR
  const cashFlows = yearlyData.map(d => d.cashFlowForIRR);
  const irr = calculateIRR(initialInvestment, cashFlows);

  // Calculate total return metrics
  const totalCashFlowReceived = yearlyData.reduce((sum, d) => sum + d.cashFlow, 0);
  const finalEquity = yearlyData[yearlyData.length - 1].equity;
  const totalReturn = totalCashFlowReceived + finalEquity;
  const totalReturnPercent = ((totalReturn - initialInvestment) / initialInvestment) * 100;

  return {
    yearlyData,
    irr: isNaN(irr) ? 0 : irr,
    initialInvestment,
    totalCashFlow: totalCashFlowReceived,
    finalEquity,
    totalReturn,
    totalReturnPercent,
    mortgageAmount: Math.round(mortgageAmount),
    monthlyMortgagePayment: Math.round(monthlyMortgagePayment),
    monthlyCarryingCosts: Math.round(monthlyCarryingCosts),
    startingRent: Math.round(monthlyRent),
    sellingCostPercent,
  };
}
